import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users, Building2, Car, FileText, Search, ChevronLeft, ChevronRight, Filter, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 15;

const TYPE_META = {
  person: { label: 'Persona', icon: <Users className="h-4 w-4" /> },
  company: { label: 'Empresa', icon: <Building2 className="h-4 w-4" /> },
  vehicle: { label: 'Vehículo', icon: <Car className="h-4 w-4" /> },
};

const statusFromScore = (score) => {
  if (score >= 90) return { key: 'approved', label: 'Aprobado', cls: 'bg-green-100 text-green-700 border-green-300' };
  if (score < 70) return { key: 'rejected', label: 'Rechazado', cls: 'bg-red-100 text-red-700 border-red-300' };
  return { key: 'review', label: 'En Revisión', cls: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
};

const riskClass = (level) => ({
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-red-600',
}[level] || 'text-gray-600');

const Historial = ({ user }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('nika-auth-token');
      if (!token) { setError('Sesión expirada'); return; }
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (searchTerm.trim()) params.q = searchTerm.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/api/validations`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(data?.validations || []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.error(e);
      setError('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, searchTerm, dateFrom, dateTo]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // Reset page to 1 when filters change
  useEffect(() => { setPage(1); }, [typeFilter, searchTerm, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const rows = useMemo(() => items.map((v) => {
    const r = v.result || {};
    const rd = v.request_data || {};
    const score = r.score ?? 100;
    const status = statusFromScore(score);
    const meta = TYPE_META[v.type] || { label: v.type, icon: <FileText className="h-4 w-4" /> };
    const name = r.raw_data?.full_name
      || `${rd.first_name || ''} ${rd.last_name || ''}`.trim()
      || rd.company_name
      || rd.plate
      || 'Consulta';
    const document = rd.document_number || rd.nit || rd.plate || '—';
    return {
      id: v.id,
      type: v.type,
      typeLabel: meta.label,
      typeIcon: meta.icon,
      name,
      document,
      date: v.created_at,
      findings: (r.findings || []).length,
      score,
      status,
      riskLevel: r.risk_level || 'low',
      raw: v,
    };
  }), [items]);

  const openDetail = (row) => {
    navigate('/reporte-completo', {
      state: {
        result: row.raw.result,
        tipo: row.type,
        datos: row.raw.request_data || {},
        validation_id: row.id,
      },
    });
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  const hasFilters = typeFilter !== 'all' || searchTerm || dateFrom || dateTo;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Historial de Validaciones</h1>
            <p className="text-gray-600 mt-1">
              {isAdmin
                ? 'Vista global: todas las consultas realizadas por usuarios de todas las empresas.'
                : 'Tus consultas. Solo tú puedes ver el historial de tus propias validaciones.'}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Volver al Dashboard
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filtros
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto h-7 px-2 text-xs" data-testid="clear-filters-btn">
                  <X className="h-3 w-3 mr-1" /> Limpiar
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="type-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="person">Personas</SelectItem>
                    <SelectItem value="company">Empresas</SelectItem>
                    <SelectItem value="vehicle">Vehículos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Búsqueda (nombre, documento, NIT, placa)</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Ej: 80732669 ó GARNICA"
                    className="pl-8"
                    data-testid="historial-search-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from-input" />
              </div>
              <div>
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Resultados</CardTitle>
                <CardDescription>
                  {loading ? 'Cargando…' : `${total} consulta${total === 1 ? '' : 's'} encontrada${total === 1 ? '' : 's'}`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="text-center py-6 text-red-600">{error}</div>
            )}
            {!error && !loading && rows.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">No se encontraron consultas</p>
                <p className="text-sm text-gray-500 mt-1">
                  {hasFilters ? 'Ajusta los filtros para ver más resultados.' : 'Aún no has realizado consultas.'}
                </p>
              </div>
            )}
            {!error && rows.length > 0 && (
              <div className="space-y-3">
                {rows.map((row) => (
                  <div
                    key={row.id}
                    onClick={() => openDetail(row)}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    data-testid={`historial-row-${row.id}`}
                  >
                    <div className="flex items-center space-x-4 min-w-0">
                      <div className="p-2 bg-white rounded-lg flex-shrink-0">{row.typeIcon}</div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">{row.name}</span>
                          <Badge variant="outline" className="text-xs">{row.typeLabel}</Badge>
                        </div>
                        <div className="text-sm text-gray-600">{row.document}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(row.date).toLocaleString('es-CO')} • {row.findings} hallazgo{row.findings === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right space-y-1 flex-shrink-0 ml-4">
                      <Badge className={row.status.cls}>{row.status.label}</Badge>
                      <div className={`text-xs font-medium ${riskClass(row.riskLevel)}`}>
                        Riesgo {row.riskLevel === 'low' ? 'Bajo' : row.riskLevel === 'medium' ? 'Medio' : 'Alto'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!error && total > PAGE_SIZE && (
              <div className="mt-6 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-gray-600">
                  Página {page} de {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    data-testid="next-page-btn"
                  >
                    Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Historial;
